// @flow
import React, { Component } from "react";
import { noop, get, cloneDeep } from "lodash";
import type { Device } from "@tvg/types/Device";
import type { RaceTemplate } from "@tvg/types/Race";
import { buildRaceUrl } from "@tvg/formatter";
import type { UnaryFn, SixFn } from "@tvg/types/Functional";
import {
  handleTouchMove,
  handleTouchStart,
  evaluateReachingEdge
} from "@tvg/utils/swipeEventHandlers";
import { getHasPromo } from "@tvg/utils/PromoUtils";
import { RaceList } from "./styled-components";
import RaceLinkAbbr from "../../_molecules/RaceLinkAbbr";

type ScrollActionArgs = {
  left: number,
  behavior: "smooth"
};

type ScrollListElement = HTMLElement & {
  scroll: UnaryFn<ScrollActionArgs, void>
};

type State = {
  disableLeftScroll: boolean,
  disableRightScroll: boolean
};

type Props = {
  device: Device,
  idPrefix: string,
  trackRaces: RaceTemplate[],
  qaLabel: string,
  trackCode: string,
  isFavorite: boolean,
  isLogged: boolean,
  sendGTMScrollEvent: UnaryFn<boolean, void>,
  onRaceSelect: SixFn<
    string,
    string,
    RaceTemplate,
    string,
    boolean,
    boolean,
    void
  >,
  isGreyhound: boolean,
  useIsPromoTagShownFlag: boolean
};

const bestPromo = ({ allowBelowTheLine, promos }) => {
  let belowTheLinePromo;
  let aboveTheLinePromo;
  promos.forEach((promo) => {
    if (!promo.isAboveTheLine && !belowTheLinePromo && allowBelowTheLine) {
      belowTheLinePromo = cloneDeep(promo);
    }

    if (promo.isAboveTheLine && !aboveTheLinePromo) {
      aboveTheLinePromo = cloneDeep(promo);
    }
  });
  return belowTheLinePromo || aboveTheLinePromo;
};

export const findMissingScrollAmount = (
  currentScrollAmount: number
): number => {
  const stringValue = currentScrollAmount.toString();
  return +stringValue.substring(stringValue.length - 2, stringValue.length);
};

/* eslint-disable react/no-unused-state */
export default class TrackLine extends Component<Props, State> {
  static defaultProps = {
    device: "mobile",
    idPrefix: "",
    trackRaces: [],
    qaLabel: "",
    trackCode: "",
    isFavorite: false,
    sendGTMScrollEvent: noop,
    onRaceSelect: noop,
    isGreyhound: false,
    isLogged: false,
    useIsPromoTagShownFlag: false
  };

  scrollTimeout: ?TimeoutID;

  raceList: ScrollListElement;

  shallDisplayNavigationArrows: boolean;

  SCROLL_AMOUNT: number;

  constructor(props: Props) {
    super(props);
    this.SCROLL_AMOUNT = 100;
    this.shallDisplayNavigationArrows = true;
    this.state = {
      disableLeftScroll: true,
      disableRightScroll: true
    };
    this.scrollTimeout = null;
  }

  componentDidMount() {
    const tempRaceList: HTMLElement | null = document.getElementById(
      `${this.props.idPrefix}_racesContainer-${this.props.trackCode}`
    );
    if (tempRaceList !== null) {
      const navigationStatus = {
        disableLeftScroll: tempRaceList.scrollLeft === 0,
        disableRightScroll:
          tempRaceList.offsetWidth + tempRaceList.scrollLeft >=
          tempRaceList.scrollWidth
      };
      /* eslint-disable */ this.setState({
        ...navigationStatus
      }); /* eslint-enable */
      this.shallDisplayNavigationArrows = !navigationStatus.disableRightScroll;
      if (
        this.shallDisplayNavigationArrows &&
        this.props.device === "desktop"
      ) {
        tempRaceList.addEventListener("scroll", this.removeEventAfterScroll);
      }
    }
  }

  // hack to detect scroll move on beginnig
  removeEventAfterScroll = (event: Event) => {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      this.scrollRaceList(0);
      event.target.removeEventListener("scroll", this.removeEventAfterScroll);
    }, 50);
  };

  scrollRaceList = (amount: number) => {
    this.setState({
      disableLeftScroll: this.raceList.scrollLeft + amount <= 0,
      disableRightScroll:
        this.raceList.offsetWidth + this.raceList.scrollLeft + amount >=
        this.raceList.scrollWidth
    });
    if (amount !== 0) {
      this.raceList.scroll({
        left: this.raceList.scrollLeft + amount,
        behavior: "smooth"
      });
    }
  };

  scrollForward = () => {
    this.props.sendGTMScrollEvent(false);
    this.scrollRaceList(this.SCROLL_AMOUNT);
  };

  scrollBack = () => {
    const amountToScroll = this.state.disableRightScroll
      ? -findMissingScrollAmount(this.raceList.scrollLeft)
      : -this.SCROLL_AMOUNT;
    this.props.sendGTMScrollEvent(true);
    this.scrollRaceList(amountToScroll);
  };

  handleSwipeGTMEvents = (e: SyntheticTouchEvent<*>) => {
    let swipeDirection = handleTouchMove(e);
    const isLivingOnTheEdge = evaluateReachingEdge(e);
    swipeDirection = isLivingOnTheEdge ? 0 : swipeDirection;
    if (swipeDirection !== 0) {
      this.props.sendGTMScrollEvent(swipeDirection < 0);
    }
  };

  renderTrackRaceLinks = () =>
    this.props.trackRaces.map((trackRace: RaceTemplate) => {
      const userPromotions = get(trackRace, "userPromotions", []);
      let aboveTheLine = null;
      let belowTheLine = null;

      userPromotions.forEach((promo) => {
        if (!promo.isAboveTheLine && !belowTheLine) {
          belowTheLine = promo;
        }

        if (promo.isAboveTheLine && !aboveTheLine) {
          aboveTheLine = promo;
        }
      });

      const tracksPromos = get(trackRace, "promos", []);
      const promo = bestPromo({
        allowBelowTheLine: this.props.isLogged,
        promos: tracksPromos
      });
      const userPromo = belowTheLine || aboveTheLine;

      const hasPromo = get(promo, "isAboveTheLine", false) || !!userPromo;

      const isPromoTagShown =
        !!get(userPromo, "isPromoTagShown", false) ||
        !!get(promo, "isPromoTagShown", false);

      const shouldShowPromo = getHasPromo(
        this.props.useIsPromoTagShownFlag,
        hasPromo,
        isPromoTagShown
      );

      const isOptedIn =
        get(userPromo, "optedIn", false) || get(promo, "optedIn", false);
      let promoState = "no promo";

      if (isOptedIn || shouldShowPromo) {
        promoState = isOptedIn ? "promo optin" : "promo";
      }

      const programPageURL = buildRaceUrl(
        this.props.trackCode,
        trackRace.trackName,
        trackRace.number,
        trackRace.isGreyhound
      );

      const id =
        this.props.idPrefix && this.props.trackCode && trackRace.number
          ? `${this.props.idPrefix}_raceElement_${this.props.trackCode}_${trackRace.number}`
          : "";

      return (
        <li
          id={id}
          key={`RaceLinkAbbr_${trackRace.number}`}
          data-qa-label={`topTrackRace${trackRace.number}Item`}
        >
          <RaceLinkAbbr
            onClickCallback={() => {
              this.props.onRaceSelect(
                programPageURL,
                trackRace.trackName,
                trackRace,
                promoState,
                this.props.isFavorite,
                this.props.isGreyhound
              );
            }}
            // TODO: Must remove '+' after updating @tvg/types package
            raceNumber={+trackRace.number}
            mtpPostTime={trackRace.postTime}
            inPromo={hasPromo}
            optedIn={isOptedIn || shouldShowPromo}
            qaLabel="topTrackRace"
            mtp={trackRace.mtp}
            mtpStatus={trackRace.raceStatus}
            url={programPageURL}
          />
        </li>
      );
    });

  render() {
    return (
      <RaceList
        id={`${this.props.idPrefix}_racesContainer-${this.props.trackCode}`}
        onTouchStart={(e: SyntheticTouchEvent<*>) => handleTouchStart(e)}
        onTouchMove={(e: SyntheticTouchEvent<*>) =>
          this.handleSwipeGTMEvents(e)
        }
        data-qa-label={`${this.props.qaLabel}RaceList`}
      >
        {this.renderTrackRaceLinks()}
      </RaceList>
    );
  }
}
/* eslint-enable react/no-unused-state */
